import React, { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { artistAPI, resolveMediaSrc } from '@/lib/api';
import { toast } from 'sonner';
import { Music, Save, Play } from 'lucide-react';
import AppShell from '@/components/AppShell';

const C = { card: '#1A2236', gold: '#D4A853', cream: '#F8F5EE', muted: '#94A3B8', green: '#34D399' };
const inputStyle = { background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: C.cream };

const STATUS_LABEL = {
  pending: 'In review. Children cannot see your profile or songs yet.',
  approved: 'Approved. Your profile and approved songs are visible in the children\'s music library.',
};

const ArtistPortal = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const profileQuery = useQuery({ queryKey: ['artist-profile'], queryFn: async () => (await artistAPI.getProfile()).data });
  const songsQuery = useQuery({ queryKey: ['artist-songs'], queryFn: async () => (await artistAPI.getSongs()).data });
  const profile = profileQuery.data;
  const [form, setForm] = useState({ stage_name: '', bio: '', country: '', genres: '', website: '' });

  useEffect(() => {
    if (profile) {
      setForm({ stage_name: profile.stage_name || '', bio: profile.bio || '', country: profile.country || '',
        genres: (profile.genres || []).join(', '), website: profile.website || '' });
    }
  }, [profile]);

  const save = useMutation({
    mutationFn: () => artistAPI.updateProfile({ ...form, genres: form.genres.split(',').map((g) => g.trim()).filter(Boolean) }),
    onSuccess: () => { toast.success('Profile saved'); queryClient.invalidateQueries({ queryKey: ['artist-profile'] }); },
    onError: (e) => toast.error(e.response?.data?.detail || 'Could not save the profile'),
  });

  const play = async (id) => {
    try { new Audio(await resolveMediaSrc(`/api/media/gridfs/${id}`)).play(); } catch (e) { toast.error('Could not play that song'); }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const songs = songsQuery.data || [];

  return (
    <AppShell title="Artist Portal" subtitle={profile?.stage_name || user?.full_name} onLogout={() => { logout(); navigate('/login'); }}>
      <div className="container mx-auto px-3 sm:px-4 py-6 grid gap-6 lg:grid-cols-2" data-testid="artist-portal">
        <section className="p-5 rounded-2xl min-w-0" style={{ background: C.card, border: '1px solid rgba(255,255,255,0.08)' }}>
          <h2 className="text-lg font-bold mb-1" style={{ color: C.cream }}>Your profile</h2>
          {profile && (
            <p className="text-sm mb-4" style={{ color: profile.status === 'approved' ? C.green : C.gold }} data-testid="artist-status">
              {STATUS_LABEL[profile.status]}
            </p>
          )}
          <form onSubmit={(e) => { e.preventDefault(); save.mutate(); }} className="grid gap-3">
            <label className="grid gap-1 text-sm" style={{ color: C.muted }}>Stage name
              <input required maxLength={80} value={form.stage_name} onChange={set('stage_name')} className="rounded-lg px-3 py-2" style={inputStyle} data-testid="artist-name" />
            </label>
            <label className="grid gap-1 text-sm" style={{ color: C.muted }}>About you (shown to children)
              <textarea rows={4} maxLength={1000} value={form.bio} onChange={set('bio')} className="rounded-lg px-3 py-2" style={inputStyle} data-testid="artist-bio" />
            </label>
            <label className="grid gap-1 text-sm" style={{ color: C.muted }}>Country
              <input maxLength={60} value={form.country} onChange={set('country')} className="rounded-lg px-3 py-2" style={inputStyle} />
            </label>
            <label className="grid gap-1 text-sm" style={{ color: C.muted }}>Genres (comma separated)
              <input value={form.genres} onChange={set('genres')} className="rounded-lg px-3 py-2" style={inputStyle} />
            </label>
            <label className="grid gap-1 text-sm" style={{ color: C.muted }}>Website (only you and the admin see this)
              <input type="url" placeholder="https://" value={form.website} onChange={set('website')} className="rounded-lg px-3 py-2" style={inputStyle} />
            </label>
            <p className="text-xs" style={{ color: C.muted }}>Changing your stage name or About text sends the profile back to review.</p>
            <button type="submit" disabled={save.isPending} className="flex items-center justify-center gap-2 rounded-lg px-4 py-2 font-semibold text-black" style={{ background: C.gold }} data-testid="artist-save">
              <Save size={16} /> {save.isPending ? 'Saving…' : 'Save profile'}
            </button>
          </form>
        </section>

        <section className="p-5 rounded-2xl min-w-0" style={{ background: C.card, border: '1px solid rgba(255,255,255,0.08)' }}>
          <h2 className="text-lg font-bold mb-4 flex items-center gap-2" style={{ color: C.cream }}><Music size={18} /> Your songs ({songs.length})</h2>
          {songs.length === 0 ? (
            <p className="text-sm" style={{ color: C.muted }}>No songs yet. Songs are added by the label or the admin, and appear here once linked to your profile.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" style={{ color: C.cream }}>
                <thead><tr className="text-left" style={{ color: C.muted }}>
                  <th className="py-2 pr-3">Title</th><th className="pr-3"></th><th className="pr-3">Status</th><th className="pr-3">Plays</th><th className="pr-3">Downloads</th><th>Likes</th>
                </tr></thead>
                <tbody>
                  {songs.map((s) => (
                    <tr key={s.id} style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                      <td className="py-2 pr-3">{s.title}</td>
                      <td className="pr-3"><button onClick={() => play(s.id)} aria-label={`Play ${s.title}`} style={{ color: C.gold }}><Play size={14} /></button></td>
                      <td className="pr-3">{s.status}</td>
                      <td className="pr-3">{s.total_streams || 0}</td>
                      <td className="pr-3">{s.total_downloads || 0}</td>
                      <td>{s.total_likes || 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </AppShell>
  );
};

export default ArtistPortal;
